import React, { useCallback, useEffect, useState, useRef } from 'react'
import { PayPalScriptProvider, usePayPalScriptReducer } from '@paypal/react-paypal-js'
import { api } from 'utils'
import { PAYPAL, PAYPAL_CREATE_ORDER, PAYPAL_CAPTURE_ORDER } from 'utils/endpoint'
import { useTrans } from 'hooks'
import { FormError } from 'components/atoms'
import * as Sentry from "@sentry/browser"


export const fetchPaypalCredentials = () => {
  return api
  .get(`${PAYPAL}`)
  .then((res) => {
    const data = res.data
    return data?.client_token
  })
  .catch((err) => {
    console.log(err)
  })
}

const PaypalFormContainer = (props) => {

  const [clientToken, setClientToken] = useState<any>()

  useEffect(() => {
    fetchPaypalCredentials()
    .then((data) => {
      setClientToken(data)
    })
  }, [])
  const initialOptions = {
    'debug': process.env.NODE_ENV === 'development' ? true : false,
    'currency': 'EUR',
    'client-id': process.env.PAYPAL_CLIENT_ID,
    'data-client-token': clientToken,
    'components': 'hosted-fields',
    'data-namespace': 'paypal_form'
  }

  if(!clientToken) 
    return null

  return (
    <PayPalScriptProvider options={initialOptions}>
      <PaypalForm confirmOrder={props.confirmOrder} disabled={props.disabled} />
    </PayPalScriptProvider>
  )
}

const PaypalForm = ({ confirmOrder, disabled }) => {
  const paypal = typeof window !== 'undefined' && window.paypal_form
  const [{ isResolved, options }] = usePayPalScriptReducer()
  const [supportsHostedFields, setSupportsHostedFields] = useState(null)
  const [error, setError] = useState("")
  const inProgress = useRef<boolean>()

  const orderIdRef = useRef()

  const t = useTrans()

  const submitAndCapture = (cardFields) => {
    if(inProgress?.current) return
    inProgress.current = true

    const state = cardFields.getState()
    const formValid = Object.keys(state.fields).every(function (key) {
      return state.fields[key].isValid
    })

    if(!formValid) {
      Object.keys(state.fields).forEach((key) => {
        Sentry.addBreadcrumb({
          category: "message",
          message: key + ": " + JSON.stringify({...state.fields[key], container: null}),
          level: Sentry.Severity.Info,
        })
      })
      Sentry.captureMessage("PaypalForm - formValidation error")
      setError(t("I campi immessi non sono validi"))
      console.log('form_validation_error', formValid)
      inProgress.current = false
      return
    }

    let cardholderName = ""
    try {
    //@ts-ignore
    cardholderName = document.getElementById('card-holder-name').value
    } catch(err) {
      Sentry.addBreadcrumb({
        category: "message",
        message: JSON.stringify(err.message),
        level: Sentry.Severity.Info,
      })
      Sentry.captureMessage("PaypalForm - cardholderName error")
      console.log("cardholderName_error", err)
      inProgress.current = false
      return
    }
    
    cardFields.submit({
      cardholderName: cardholderName,
      // Trigger 3D Secure authentication
      contingencies: ['3D_SECURE'],
    }).then(function (payload) {
      Sentry.addBreadcrumb({
        category: "message",
        message: JSON.stringify(payload),
        level: Sentry.Severity.Info,
      })
      Sentry.captureMessage("PaypalForm - frontend submit response", Sentry.Severity.Info)
      
      // 3D Secure passed successfully
      if (payload.liabilityShift === "YES" || payload.liabilityShift === "POSSIBLE") {
        
        api.get(`${PAYPAL}${orderIdRef.current}/${PAYPAL_CAPTURE_ORDER}`)
        .then((res) => {
            // Show a success message or redirect
            setError("")
            confirmOrder()
        })
        .catch((err) => {
          Sentry.captureException(err)
          setError(t("Impossibile confermare il pagamento"))
          console.log('capture_order_error', err)
          inProgress.current = false
          return
        })
      }
      else {
        setError(t("Errore nell'autorizzazione del pagamento"))
        console.log('3d_secure_error', payload)
        inProgress.current = false
        return
      }
      
    })
    .catch((err) => {
      let errorMessage = t("Errore nel processare il pagamento") + "\n"
      if(err?.details && Array.isArray(err.details)) {
        err.details.forEach((item) => {
          if(item.field && item.description) {
            errorMessage += '- ' + t(item.field) + ': ' + t(item.description) + ';\n'
          }
          Sentry.addBreadcrumb({
            category: "message",
            message: JSON.stringify(item),
            level: Sentry.Severity.Error,
          })
        })
      }
      setError(errorMessage)
      Sentry.captureException(err)
      console.log('submit_error', err)
      inProgress.current = false
      return
    })
  }

  const initPaypal = useCallback(async () => {
    try {
      if (paypal && typeof supportsHostedFields === 'boolean' && !!supportsHostedFields) {
        paypal?.HostedFields?.render({
          createOrder: function () {
            return api.get(`${PAYPAL_CREATE_ORDER}`)
            .then((res) => {
              orderIdRef.current = res.data.id
              return res.data.id
            })
            .catch((err) => {
              Sentry.captureException(err)
              setError(t("Errore nella creazione dell'ordine"))
              console.log('create_order_error', err)
            })
          },
          styles: {
            '.valid': {
             'color': '#18966A'
            },
            '.invalid': {
             'color': '#B4282C'
            }
          },
          fields: {
            number: {
              selector: "#card-number",
              placeholder: "4111 1111 1111 1111"
            },
            cvv: {
              selector: "#cvv",
              placeholder: "123"
            },
            expirationDate: {
              selector: "#expiration-date",
              placeholder: "MM/YY"
            }
          },
        }).then(function (cardFields) {
          setTimeout(() => {          
            document.querySelector('#paypal-form-submit-button').addEventListener('click', (event) => {
              event.preventDefault()
              submitAndCapture(cardFields)
            })
            document.querySelector('#paypal-form-submit-button-mobile').addEventListener('click', (event) => {
              event.preventDefault()
              submitAndCapture(cardFields)
            })
          }, 800)
        })

      }
    } catch (error) {
      Sentry.captureException(error)
      console.log('init_paypal_error', error)
    }
  }, [paypal, supportsHostedFields])

  useEffect(() => {
    if (isResolved) {
      const isEligible = paypal?.HostedFields.isEligible()
      if (isEligible) {
        setSupportsHostedFields(true)
      }
      else {
        setSupportsHostedFields(false)
        console.log("isEligible", isEligible)
        Sentry.captureException(new Error("isEligible is false"))
      }
      
    }
  }, [setSupportsHostedFields, options, isResolved, paypal])

  useEffect(() => {
    initPaypal()
  }, [initPaypal])

  if(!isResolved) {
    return null
  }

  if(!supportsHostedFields) {
    return null
  }

  return (
    <>
        <form id="paypal-form" >
          <div className="paypal-form__row">
            <div>
              <label htmlFor="card-number" className="paypal-form-input-label">{t('Numero di carta')}</label>
              <div id="card-number" className="paypal-form-input"></div>
            </div>
          </div>
          <div className="paypal-form__row">
            <div>
              <label htmlFor="expiration-date" className="paypal-form-input-label">{t('Data di scadenza')}</label>
              <div id="expiration-date" className="paypal-form-input"></div>
            </div>
            <div>
              <label htmlFor="cvv" className="paypal-form-input-label">CVV</label>
              <div id="cvv" className="paypal-form-input"></div>
            </div>
          </div>
          <div className="paypal-form__row">
            <div>
              <label htmlFor="card-holder-name" className="paypal-form-input-label">{t('Nome sulla carta')}</label>
              <input type="text" id="card-holder-name" name="card-holder-name" autoComplete="off" className="paypal-form-input"/>
            </div>
          </div>
          {error &&
          <div style={{whiteSpace: "pre-line"}}>
            <FormError 
              message={error}
              visible={error !== ""}
            />
          </div>
          }
        </form>
    </>
  )
}

export default PaypalFormContainer
